import config from '@/shared/config/config';
import useSettings from '@/shared/lib/hooks/useSettings';
import IdleDialog from '@/shared/ui/components/Dialogs/Auth/IdleDialog';
import { useModal } from 'mui-modal-provider';
import { useSnackbar } from 'notistack';
import { useEffect, useState } from 'react';
import { useIdleTimer } from 'react-idle-timer';

type Props = {
  handleLogout: (timedOut?: boolean) => void;
  timeoutOverrideInSeconds?: number;
  promptTimeoutOverrideInSeconds?: number;
  disablePinPrompt?: boolean;
};

export const useIdleModal = ({
  handleLogout,
  timeoutOverrideInSeconds,
  promptTimeoutOverrideInSeconds,
  disablePinPrompt = false
}: Props) => {
  const { settings, isSecurityPinSet } = useSettings();
  const { showModal } = useModal();
  const { enqueueSnackbar } = useSnackbar();

  const [timeoutInSeconds, setTimeoutInSeconds] = useState<number>(
    timeoutOverrideInSeconds && timeoutOverrideInSeconds > 0 ? timeoutOverrideInSeconds : config.INACTIVITY_TIMEOUT_S
  );
  const [promptTimeoutInSeconds, setPromptTimeoutInSeconds] = useState<number>(
    promptTimeoutOverrideInSeconds && promptTimeoutOverrideInSeconds > 0
      ? promptTimeoutOverrideInSeconds
      : config.INACTIVITY_PROMPT_TIMEOUT_S
  );

  const showIdleModal = () => {
    const modalRef = showModal(IdleDialog, {
      title: 'You are about to be logged out',
      countFrom: promptTimeoutInSeconds,
      askForPin: Number(settings['inactivityPinPromptTimeoutSeconds'] ?? 0) > 0 && isSecurityPinSet && !disablePinPrompt,
      hideDialog: () => {
        idleTimer.activate();
        modalRef.hide();
      },
      logout: ({ showErrorMessage = true }: { showErrorMessage?: boolean }) => {
        if (showErrorMessage) {
          enqueueSnackbar('Your session has timed out', { variant: 'warning' });
        }
        idleTimer.reset();
        handleLogout(showErrorMessage);
        modalRef.hide();
      }
    });
  };

  const onPrompt = () => {
    showIdleModal();
  };

  const onIdle = () => {
    handleLogout(true);
  };

  const idleTimer = useIdleTimer({
    onIdle,
    onPrompt,
    crossTab: true,
    leaderElection: true,
    syncTimers: 200,
    timeout: (timeoutInSeconds + promptTimeoutInSeconds) * 1000,
    promptBeforeIdle: promptTimeoutInSeconds * 1000,
    disabled: ((settings['inactivityTimeoutSeconds'] as number) ?? 1) <= 0
  });

  // Update the timeouts based on settings
  useEffect(() => {
    const inactivityTimeoutSeconds =
      timeoutOverrideInSeconds && timeoutOverrideInSeconds > 0
        ? timeoutOverrideInSeconds
        : (settings['inactivityTimeoutSeconds'] as number);
    const inactivityPinPromptTimeoutSeconds =
      promptTimeoutOverrideInSeconds && promptTimeoutOverrideInSeconds > 0
        ? promptTimeoutOverrideInSeconds
        : (settings['inactivityPinPromptTimeoutSeconds'] as number);

    setTimeoutInSeconds(inactivityTimeoutSeconds > 0 ? inactivityTimeoutSeconds : config.INACTIVITY_TIMEOUT_S);
    setPromptTimeoutInSeconds(
      inactivityPinPromptTimeoutSeconds > 0 ? inactivityPinPromptTimeoutSeconds : config.INACTIVITY_PROMPT_TIMEOUT_S
    );
  }, [settings]);

  return { idleTimer, showIdleModal };
};
